import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaEdit, FaCircle } from "react-icons/fa";
import { api } from "../../api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { customStyles } from "../../styles/dataTableStyles";

const AdminAgents = ({ addAccess }) => {
  const token = useSelector((state) => state.auth.token);
  // eslint-disable-next-line
  const { userType: role } = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [listingReset, setListingReset] = useState(false);


  const fetchAgents = useCallback(async (pageNo, searchKey, pageSize) => {
    try {
      setLoading(true);
      const response = await api.users.getAgentList(
        { pageNo, pageSize, sort: "asc", sortBy: "usersName", searchKey, usersUserType: 2 },
        token
      );
      if (response.result) {
        setAgents(response.result);
        setTotalRows(response.totalCount);
      } else {
        setAgents([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching agents", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
        setPage(1);
      // eslint-disable-next-line
    }, [search]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchAgents(page, search, pageSize);
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [page, search, pageSize, listingReset, fetchAgents]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to inactive this agent?",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Okay",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await api.users.delete({ delete_array: [id], status: false }, token);
          if (res.success) {
            toast.success("Agent deactivated successfully");
            setListingReset((prev) => !prev);
          } else {
            toast.error(res.message || "Failed to delete agent");
          }
        } catch (error) {
          toast.error("Something went wrong");
        }
      }
    });
  };

  const handleActivate = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to activate this agent?",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Activate",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await api.users.delete({ delete_array: [id], status: true }, token);
          if (res.success) {
            toast.success("Agent activated successfully");
            setListingReset((prev) => !prev);
          } else {
            toast.error(res.message || "Failed to activate agent");
          }
        } catch (error) {
          toast.error("Something went wrong");
        }
      }
    });
  };

  const columns = [
    { name: "ID", selector: row => row.usersId, sortable: true, width: "80px" },
    { name: "Agent Name", selector: row => row.usersName, sortable: true },
    { name: "Phone", selector: row => row.usersPhone, width: "150px" },
    { name: "Email", selector: row => row.usersEmail},
    {
      name: "Status",
      selector: row => row.usersStatus,
      cell: row => (
        <span style={{
          padding: "5px 10px",
          borderRadius: "5px",
          color: row.usersStatus ? "#28a745" : "#dc3545",
          display: "inline-block",
          minWidth: "80px",
          textAlign: "center"
        }}>
          {row.usersStatus ? "Active" : "Inactive"}
        </span>
      ),
      width: "100px"
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {row.usersStatus ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/view-agent/${row.usersId}`)}>View</button>
              <button className="listButton" onClick={() => navigate(`/edit-agent/${row.usersId}`)}>
                <FaEdit />
              </button>
              <button title="Click to Inactive" className="listButton" onClick={() => handleDelete(row.usersId)}>
                <FaCircle style={{ fill: 'green' }} />
              </button>
            </>
          ) : (
            <button title="Click to Active" className="listButton" onClick={() => handleActivate(row.usersId)}>
              <FaCircle style={{ fill: 'grey' }} />
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
     
      width: "160px"
    }
  ];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="sec-header">ADMIN AGENTS</h2>
        <div className="d-flex align-items-center">
          <label className="mr-2">Search:</label>
          <input
            type="text"
            placeholder="Agent Name"
            className="form-control"
            style={{ width: "200px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn new-btn-top btn-primary"
            style={{marginLeft: "10px", marginTop: "15px" }}
            onClick={() => navigate("/add-agent")}
          >
            ADD AGENT
          </button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={agents}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize, newPage) => {
          setPageSize(newPageSize);
          setPage(newPage);
        }}
        highlightOnHover
        customStyles={customStyles}
        responsive
        wrap
      />
    </div>
  );
};

export default AdminAgents;
